import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Button,
  CalendarAddXIcon,
  Caption1,
  IconButton,
  Tooltip,
  WarningTriangleSmallIcon,
} from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { getPersonalizedDateFormat } from "~/store/selectors/selector.configOptions";
import { formatDateRange, formatDateSmart } from "~/utils/date";
import useAcSelectDate from "../Hooks/useAcSelectDate";
import SelectDates from "../Selectors/SelectDates";

export const SubtaskDatePicker = ({
  dueOn,
  isCompleted,
  handleDateChange,
  changeMode,
  userId,
  taskDueOn,
  canModify,
  className,
}) => {
  const format = useSelector(getPersonalizedDateFormat);
  const { today } = useAcSelectDate();
  const { t } = useTranslation();
  const isOverdue = dueOn < today.clone().startOf("day").unix() && !isCompleted;
  const isInConflict = taskDueOn && dueOn > taskDueOn;
  const fullDueDate = formatDateSmart(dueOn, format, true);
  const formattedDate = formatDateRange(dueOn, dueOn, format);

  const renderDateContent = useCallback(() => {
    if (dueOn) {
      return (
        <span className={className}>
          <Tooltip title={isInConflict ? t("Subtask should be completed before task's due date.") : fullDueDate}>
            <Button
              type="button"
              variant="text gray"
              className="tw-rounded tw-w-full tw-px-2 tw-flex tw-items-center"
            >
              <Caption1
                weight="bold"
                lineHeight="loose"
                className={classNames("tw-flex tw-items-center", {
                  "tw-text-red-alert": isOverdue || isInConflict,
                })}
              >
                {isInConflict ? <WarningTriangleSmallIcon className="tw-mr-2" /> : null}
                {formattedDate}
              </Caption1>
            </Button>
          </Tooltip>
        </span>
      );
    }
    return (
      <IconButton
        type="button"
        variant="text gray"
        className="no_due_on"
      >
        <Tooltip title={t("Due date")}>
          <CalendarAddXIcon />
        </Tooltip>
      </IconButton>
    );
  }, [dueOn, t, className, isInConflict, fullDueDate, isOverdue, formattedDate]);

  if (!canModify) {
    return (
      <Tooltip title={fullDueDate}>
        <Caption1
          lineHeight="loose"
          className={classNames("tw-px-2 tw-h-6 tw-pt-[1px]", {
            "tw-text-red-alert": isOverdue || isInConflict,
          })}
          weight="bold"
        >
          {isInConflict ? <WarningTriangleSmallIcon className="tw-mr-2 tw-fill-red-alert" /> : null}
          {formattedDate}
        </Caption1>
      </Tooltip>
    );
  }

  return (
    <SelectDates
      trigger={renderDateContent}
      userId={userId}
      selectedDays={{ from: null, to: dueOn }}
      onChange={handleDateChange}
      changeMode={changeMode}
      selectionMode="daily"
    />
  );
};

SubtaskDatePicker.propTypes = {
  dueOn: PropTypes.number,
  isCompleted: PropTypes.bool,
  handleDateChange: PropTypes.func,
  changeMode: PropTypes.string,
  userId: PropTypes.number,
  taskDueOn: PropTypes.number,
  canModify: PropTypes.bool,
  className: PropTypes.string,
};
